import { useEffect, useState } from 'react';
import { useIsAuthenticated } from '@azure/msal-react';
import { useAccessToken } from '../auth/use-access-token.js';
import { apiFetch, ApiError, NotSignedIn } from './client.js';

/**
 * Every lesson on the account, in one list.
 *
 * `scope=all` because the screen is one list: what is coming, nearest first,
 * then what has been. Asking in two requests would mean the portal imposing an
 * order the API already knows, and two loading states for one list.
 *
 * Every person on the account is in here, the account holder included — an
 * adult who trains is an athlete like any other. There is no second route to a
 * lesson through a person, which is what keeps a lesson one push away.
 */

export interface Attendee {
  client_id: string;
  name: string;
  attended: boolean | null;
  /** This athlete's own lesson record. */
  lesson_id: string | null;
  lesson_status: string | null;
  /** A closed lesson can no longer be cancelled. */
  lesson_closed: boolean;
  /** Null unless this athlete's lesson was cancelled. */
  cancellation: { by: 'customer' | 'coach'; at: string; message: string | null } | null;
  /** The coach's note and plan, as the editor's HTML. The note is null until the lesson is closed; the plan is read as soon as it is written. */
  notes: string | null;
  practice_plan: string | null;
  /** Swings Recul counted in this lesson; null while it never counted. */
  swing_count: number | null;
}

export interface BookedLesson {
  booking_id: string;
  lesson_id: string | null;
  lesson_status: string | null;
  booking_status: string;
  slot_start: string;
  slot_end: string;
  service_name: string;
  /** The product sold — "One to one", "Group of 2". What the Type sort reads. */
  product_name: string | null;
  /** A clinic's product_name is its title. */
  is_clinic: boolean;
  facility_name: string;
  coach_name: string;
  attendees: Attendee[];
}

export type BookingsState =
  | { status: 'loading' }
  | { status: 'ready'; lessons: BookedLesson[] }
  | { status: 'failed'; message: string };

export function useBookings(): { state: BookingsState; reload: () => void } {
  const isAuthenticated = useIsAuthenticated();
  const getToken = useAccessToken();
  const [state, setState] = useState<BookingsState>({ status: 'loading' });
  const [nonce, setNonce] = useState(0);

  useEffect(() => {
    if (!isAuthenticated) return;

    let cancelled = false;
    setState({ status: 'loading' });

    apiFetch<{ bookings: BookedLesson[] }>('/api/bookings?scope=all', getToken)
      .then((body) => {
        if (!cancelled) setState({ status: 'ready', lessons: body.bookings });
      })
      .catch((cause: unknown) => {
        if (cancelled) return;
        if (cause instanceof NotSignedIn) return;
        setState({
          status: 'failed',
          message:
            cause instanceof ApiError
              ? cause.message
              : 'The API did not answer. Is the Functions host running on port 7071?',
        });
      });

    return () => {
      cancelled = true;
    };
  }, [isAuthenticated, getToken, nonce]);

  return { state, reload: () => setNonce((n) => n + 1) };
}

/**
 * Who a row names: the children still going. A child whose lesson was
 * cancelled stays on the lesson page as a record, but "Noa and Eli" on a row
 * when only Eli is going would be wrong. When everyone was cancelled the row
 * names who was booked, beside its "Cancelled".
 */
export function whoIsGoing(lesson: BookedLesson): Attendee[] {
  const going = lesson.attendees.filter((attendee) => attendee.cancellation === null);
  return going.length > 0 ? going : lesson.attendees;
}

export { outcomeOf, attendeeOutcome, type Outcome } from './lesson-outcome.js';
